import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TFiltersState } from './filtersSlice';
import { EDirection, EStatus } from './enumTypes';
import httpClient from '../../api/httpClient';
import { getFilteredByKey } from './helpers';

export type TPaymentData = {
  paymentId: string;
  clientid: number;
  bankId: number;
  corrBankAccId: number;
  corrBankName: string | null
  status: EStatus;
  errorCode: string;
  direction: EDirection;
  amount: number;
  currency: string;
  valueDate: string;
  execDate: string;
  origDetails: {
    name: string;
    street: string;
    buildingNumber: string;
    town: string;
    country: string;
    accountNumber: string;
    bankName: string;
    bankId: string;
    bankIdType: string;
  };
  benDetails: {
    name: string;
    street: string;
    buildingNumber: string;
    town: string;
    country: string;
    accountNumber: string;
    bankName: string;
    bankId: string;
    bankIdType: string;
  };
  paymentInfo: {
    purposeCode: string;
    purposeType: string;
    remittanceInfo: string;
  };
  fees: {
    paymentFeeType: string;
    clientFeeAmount: number;
    clientFeeCurrency: string;
    corrBankFeeAmount: number;
    corrBankFeeCurrency: string;
  } | null;
  refIdBank: string;
  refIdClient: string;
};

export type TPayment = {
  data: TPaymentData[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  }
};

export type TPaymentFXData = {
  fxTrxId: string;
  refIdClient: string;
  refIdBank: string;
  bankId: number;
  corrBankName: string;
  status: EStatus;
  errorCode: string;
  rejReason: string;
  direction: string;
  valueDate: string;
  name: string;
  clientRate: number;
  bankRate: number;
  dtAccountNumber: string;
  ctAccountNumber: string;
  buyDetails: {
    currency: string;
    amountBank: number;
    amountClient: number;
  };
  sellDetails: {
    currency: string;
    amountBank: number;
    amountClient: number;
  };
}

export type TPaymentFX = {
  data: TPaymentFXData[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  }
};

export const fetchPayments = createAsyncThunk<TPayment, TFiltersState['filters']>(
  'payment/fetch',
  async (filters: TFiltersState['filters']) => {
    const filteredParamsByKey = getFilteredByKey(filters);
    try {
      const response = await httpClient.get('/api/v1/payments', { params: filteredParamsByKey })
        .then(response => {
          return response.data;
        });
      return await response;
    } catch (e) {
      return console.log('payments/fetch error' + e);
    }
  }
);

export const fetchFX = createAsyncThunk<TPaymentFX, TFiltersState['filters']>(
  'paymentFX/fetch',
  async (filters: TFiltersState['filters']) => {
    const { direction, ...props } = filters
    const filteredParamsByKey = getFilteredByKey(props);
    try {
      const response = await httpClient.get('/api/v1/fx-trx', { params: filteredParamsByKey })
        .then(response => {
          return response.data;
        });
      return await response;
    } catch (e) {
      return console.log('paymentsFX/fetch error' + e);
    }
  }
);

interface PaymentState {
  payment: TPayment | null;
  paymentFX: TPaymentFX | null;
  loading: boolean;
  error: boolean;
  messageLoading: boolean;
  messageError: boolean;
}

const initialState: PaymentState = {
  payment: null,
  paymentFX: null,
  loading: false,
  error: false,
  messageLoading: false,
  messageError: false,
};

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPayments.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPayments.fulfilled, (state, action) => {
      state.payment = action.payload;
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchPayments.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchFX.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchFX.fulfilled, (state, action) => {
      state.paymentFX = action.payload;
      state.loading = false;
      state.error = false;
    });
    builder.addCase(fetchFX.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export default paymentsSlice.reducer;